import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Clock, Instagram, Send } from 'lucide-react';
import siteInfo from '../config/siteInfo';

/**
 * نماد اعتماد الکترونیکی (اینماد)
 * تا وقتی id و code در src/config/siteInfo.js پر نشده باشد، چیزی نمایش داده نمی‌شود.
 */
const EnamadBadge = () => {
  const { id, code } = siteInfo.enamad || {};
  if (!id || !code) return null;
  const q = `id=${encodeURIComponent(id)}&Code=${encodeURIComponent(code)}`;
  return (
    <a
      referrerPolicy="origin"
      target="_blank"
      rel="noopener"
      href={`https://trustseal.enamad.ir/?${q}`}
      className="inline-block bg-white rounded-lg p-1 mt-4"
    >
      <img
        referrerPolicy="origin"
        src={`https://trustseal.enamad.ir/logo.aspx?${q}`}
        alt="نماد اعتماد الکترونیکی"
        style={{ cursor: 'pointer', maxHeight: 90 }}
        code={code}
      />
    </a>
  );
};

const linkCls = 'hover:text-amber-400 transition-colors';

export const SiteFooter = () => {
  const {
    brandName, legalName, tagline, phones, email, workingHours,
    branches, licenses, social,
  } = siteInfo;

  return (
    <footer className="bg-green-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* ستون ۱: معرفی و مشخصات هویتی + اینماد */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-white">{brandName}</h3>
            {tagline && <p className="text-gray-300">{tagline}</p>}
            {legalName && (
              <p className="text-gray-300 text-sm mt-3">صاحب کسب‌وکار: {legalName}</p>
            )}
            {licenses && licenses.businessLicenseNumber && (
              <p className="text-gray-300 text-sm mt-1">
                شماره پروانه کسب: {licenses.businessLicenseNumber}
                {licenses.unionName ? ` (${licenses.unionName})` : ''}
              </p>
            )}
            <EnamadBadge />
          </div>

          {/* ستون ۲: لینک‌های سایت + قوانین */}
          <div>
            <h4 className="font-bold mb-4 text-white">لینک‌های مفید</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/products" className={linkCls}>محصولات</Link></li>
              <li><Link to="/gold-bank" className={linkCls}>بانک طلا</Link></li>
              <li><Link to="/about-us" className={linkCls}>درباره ما</Link></li>
              <li><Link to="/wishlist" className={linkCls}>علاقه‌مندی‌ها</Link></li>
            </ul>

            <h4 className="font-bold mt-6 mb-4 text-white">قوانین و خدمات</h4>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/terms" className={linkCls}>قوانین و مقررات</Link></li>
              <li><Link to="/terms#shipping" className={linkCls}>شرایط ارسال و تحویل</Link></li>
              <li><Link to="/terms#returns" className={linkCls}>انصراف و مرجوعی</Link></li>
              <li><Link to="/terms#privacy" className={linkCls}>حریم خصوصی</Link></li>
              <li><Link to="/terms#seller" className={linkCls}>مشخصات فروشنده</Link></li>
            </ul>
          </div>

          {/* ستون ۳: اطلاعات تماس */}
          <div>
            <h4 className="font-bold mb-4 text-white">اطلاعات تماس</h4>
            <div className="space-y-3 text-gray-300">
              {phones && phones.map((p) => (
                <div key={p} className="flex items-center">
                  <Phone className="w-4 h-4 ml-2 flex-shrink-0" />
                  <a href={`tel:${p}`} className={linkCls}>{p}</a>
                </div>
              ))}
              {email && (
                <div className="flex items-center">
                  <Mail className="w-4 h-4 ml-2 flex-shrink-0" />
                  <a href={`mailto:${email}`} className={linkCls}>{email}</a>
                </div>
              )}
              {workingHours && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 ml-2 flex-shrink-0" />
                  <span className="text-sm">{workingHours}</span>
                </div>
              )}
              {branches && branches.map((b) => (
                <div key={b.title} className="mt-3">
                  <h5 className="font-bold text-white mb-1">{b.title}</h5>
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 ml-2 mt-1 flex-shrink-0" />
                    <span className="text-sm">{b.address}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ستون ۴: شبکه‌های اجتماعی */}
          <div>
            <h4 className="font-bold mb-4 text-white">شبکه‌های اجتماعی</h4>
            <div className="space-y-4">
              {social && social.instagramKerman && (
                <a
                  href={social.instagramKerman}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-gradient-to-r from-purple-600/80 to-pink-600/80 p-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="text-sm font-semibold">اینستای شعبه کرمان</span>
                </a>
              )}
              {social && social.instagramRafsanjan && (
                <a
                  href={social.instagramRafsanjan}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-gradient-to-r from-purple-600/80 to-pink-600/80 p-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="text-sm font-semibold">اینستای شعبه رفسنجان</span>
                </a>
              )}
              {social && social.telegram && (
                <a
                  href={social.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-blue-500/80 p-3 rounded-lg hover:bg-blue-600 transition-all"
                >
                  <Send className="w-5 h-5" />
                  <span className="text-sm font-semibold">کانال تلگرام</span>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
          <p>© ۱۴۰۵ {brandName}. تمامی حقوق محفوظ است.</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
